<template>
  <button
    id="profile-dropdown"
    ref="dropdown"
    class="dropdown"
    tabindex="0"
    aria-label="Profile"
    :aria-expanded="toggleFlyout"
    @click="toggleFlyout = !toggleFlyout"
    @focusout="handleFocusOut"
    @keydown.escape="handleEscape"
    @keydown.arrow-down.prevent="handleArrowDown"
    @keydown.arrow-up.prevent="handleArrowUp"
  >
    <label class="iw-visuallyhidden" for="profile-dropdown">Profile</label>
    <slot></slot>
    <div
      v-if="toggleFlyout"
      class="dropdown-menu theme-group-3"
      :class="{
        'dropdown-open': toggleFlyout
      }"
    >
      <ul v-if="getUserData" class="dropdown-container">
        <div class="dropdown-salute">Hello, {{ getUserData.profile.firstName }}!</div>
        <li class="dropdown__favorites">
          <app-link
            ref="mobile-favorites"
            :route-to="{
              name: ROUTE_NAMES.FAVORITES,
              params: { vendor: getActiveVendor ? getActiveVendor.id : null }
            }"
            class="dropdown__link"
            :use-accent-color="false"
            :disabled="!getActiveVendor || enableFavOrdersLinks"
          >
            <span class="dropdown__link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="17" viewBox="2 2 20 19">
                <g fill="currentColor" fill-rule="evenodd">
                  <g fill="currentColor" fill-rule="nonzero">
                    <path
                      d="M20.571 8.18c0-3.11-2.12-3.763-3.906-3.763-1.663 0-3.538 1.782-4.118 2.468-.268.32-.826.32-1.094 0-.58-.686-2.455-2.468-4.118-2.468-1.786 0-3.906.653-3.906 3.763 0 2.025 2.064 3.907 2.087 3.929L12 18.307l6.473-6.187c.034-.033 2.098-1.915 2.098-3.94zm1.429 0c0 2.656-2.455 4.88-2.556 4.98l-6.953 6.64c-.134.134-.312.2-.491.2s-.357-.066-.491-.2l-6.964-6.662C4.455 13.061 2 10.836 2 8.18 2 4.937 3.998 3 7.335 3 9.288 3 11.118 4.527 12 5.39 12.882 4.528 14.712 3 16.665 3 20.002 3 22 4.937 22 8.18z"
                      transform="translate(-177 -154) translate(177 154)"
                    />
                  </g>
                </g>
              </svg>
            </span>
            <span>Favorites</span>
          </app-link>
        </li>
        <li class="dropdown__order-again">
          <app-link
            ref="mobile-order-again"
            :route-to="{
              name: ROUTE_NAMES.ORDER_AGAIN,
              params: { vendor: getActiveVendor ? getActiveVendor.id : null }
            }"
            class="dropdown__link"
            :disabled="enableFavOrdersLinks"
            :use-accent-color="false"
          >
            <span class="dropdown__link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <g fill="currentColor">
                  <path
                    d="M11.999 6.02l-.003 5.246c0 .2.08.39.22.53l3.754 3.754 1.06-1.06-3.534-3.534.002-4.937h-1.5z"
                    transform="translate(0 2)"
                  />
                  <path
                    d="M13.5.02c-5.28 0-9.648 3.92-10.38 9H0l3.75 3.75L7.5 9.02H4.635c.718-4.25 4.414-7.5 8.865-7.5 4.963 0 9 4.036 9 9 0 4.962-4.037 9-9 9-2.79 0-5.377-1.263-7.097-3.465l-1.182.923c2.007 2.568 5.024 4.041 8.279 4.041 5.79 0 10.5-4.71 10.5-10.5C24 4.73 19.29.02 13.5.02z"
                    transform="translate(0 2)"
                  />
                </g>
              </svg>
            </span>
            <span>Order Again</span>
          </app-link>
        </li>
        <hr class="dropdown-divider" />
        <li>
          <app-link ref="my-account" :route-to="{ name: ROUTE_NAMES.USER_INFO }" class="dropdown__link" :use-accent-color="false">
            <span class="dropdown__link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="15" viewBox="0 0 13 15">
                <g fill="none" fill-rule="evenodd">
                  <g fill="currentColor" fill-rule="nonzero" stroke="currentColor" stroke-width=".5">
                    <path
                      d="M10.709 4.229c0 1.513-.796 2.867-2.033 3.62C11.232 8.76 13 11.2 13 14h-.981c0-3.062-2.47-5.543-5.519-5.543C3.448 8.457.978 10.937.978 14H0c0-2.8 1.768-5.24 4.32-6.151-2.332-1.42-2.734-4.667-.798-6.612C6.139-1.39 10.709.432 10.709 4.23zM8.785 6.52c1.26-1.265 1.26-3.322 0-4.587-1.263-1.269-3.31-1.269-4.57 0-1.263 1.265-1.263 3.322 0 4.587 1.254 1.26 3.316 1.26 4.57 0z"
                      transform="translate(-182 -227) translate(150 68) translate(32 159.5)"
                    />
                  </g>
                </g>
              </svg>
            </span>
            <span>My Account</span>
          </app-link>
        </li>
        <li v-if="APIType === OAPI_API_TYPE">
          <app-link ref="my-account" :route-to="{ name: ROUTE_NAMES.WALLET }" class="dropdown__link" :use-accent-color="false">
            <span class="dropdown__link-icon">
              <img src="@/assets/images/wallet-icon.svg" />
            </span>
            <span>My Wallet</span>
          </app-link>
        </li>
        <li>
          <button ref="logout" class="dropdown__link" tabindex="0" @click="logout">
            <span class="dropdown__link-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 20 21">
                <g fill="none" fill-rule="evenodd">
                  <g fill="#162B51" fill-rule="nonzero" stroke="#162B51">
                    <path
                      stroke-width=".5"
                      d="M13.563 6.438c-.126-.125-.292-.125-.417 0L10 9.582 6.854 6.437c-.125-.125-.292-.125-.417 0s-.125.292 0 .417L9.584 10l-3.146 3.146c-.125.125-.125.291 0 .416.063.063.125.084.209.084.083 0 .146-.021.208-.084L10 10.418l3.146 3.146c.062.062.125.083.208.083.084 0 .146-.021.209-.084.124-.124.124-.291 0-.416L10.416 10l3.146-3.146c.104-.104.104-.292 0-.417z"
                      transform="translate(-178 -268) translate(150 68) translate(28 200.5)"
                    />
                    <path
                      stroke-width=".7"
                      d="M10 .104C4.542.104.104 4.542.104 10c0 5.458 4.438 9.896 9.896 9.896 5.458 0 9.896-4.438 9.896-9.896 0-5.458-4.438-9.896-9.896-9.896zm0 19.208C4.854 19.313.687 15.146.687 10 .688 4.854 4.854.687 10 .687c5.146 0 9.313 4.167 9.313 9.313 0 5.146-4.167 9.313-9.313 9.313z"
                      transform="translate(-178 -268) translate(150 68) translate(28 200.5)"
                    />
                  </g>
                </g>
              </svg>
            </span>
            <span>Logout</span>
          </button>
        </li>
      </ul>
      <ul v-else class="dropdown__container-log-in">
        <li>
          <app-link
            ref="sign-in"
            :route-to="{
              name: ROUTE_NAMES.LOGIN,
              params: {
                target: getTargetRouteName,
                vendor: getActiveVendor ? getActiveVendor.id : null
              }
            }"
            class="dropdown__link"
            :use-accent-color="false"
            >Sign in</app-link
          >
        </li>
        <li>
          <app-link
            ref="register"
            :route-to="{
              name: ROUTE_NAMES.REGISTRATION,
              params: {
                target: $route.name,
                vendor: getActiveVendor ? getActiveVendor.id : null
              }
            }"
            class="dropdown__link"
            :use-accent-color="false"
            >Register</app-link
          >
        </li>
      </ul>
    </div>
  </button>
</template>

<!-- prettier-ignore -->
<script>

import { mapActions, mapGetters } from "vuex";
import AppLink from "@/components/base/app-link/AppLink";
import { ROUTE_NAMES, DISPLAY } from "@/../app-constants";
import { ManageClick, OAPI_API_TYPE } from "../../../utils";

export default {
  name: "HeaderDropdown",
  components: { AppLink },
  data() {
    return {
      color: "#000",
      toggleFlyout: false,
      ROUTE_NAMES,
      elementsList: []
    };
  },
  updated() {
    this.elementsList = this.getElementsList();
  },
  computed: {
    ...mapGetters("user", ["getUserData"]),
    ...mapGetters("venueSettings", ["APIType"]),
    ...mapGetters("vendor", ["getActiveVendor"]),
    enableFavOrdersLinks() {
      return this.$route.name === ROUTE_NAMES.VENDOR_PICKER || this.$route.name === ROUTE_NAMES.VENUE_CONTAINER;
    },
    getTargetRouteName() {
      return this.$route.params.target || this.$route.name;
    }
  },
  created() {
    this.color = this.getAccentColor;
     this.OAPI_API_TYPE = OAPI_API_TYPE;
  },
  mounted() {
    this.manageClick = new ManageClick("body", this.hideFlyout);
    this.elementsList = this.getElementsList();
  },
  unmounted() {
    this.manageClick.removeListeners();
    this.manageClick = null;
  },
  methods: {
    ...mapActions("user", ["logOutUser"]),
    logout() {
      try {
        this.logOutUser();
        this.$router.push({ name: "Login" });
      } catch (error) {
        console.error(error);
      }
    },
    isDesktop() {
      return window.innerWidth >= DISPLAY.minDesktopWidth;
    },
    getElementsList() {
      const elements = [
        { ref: this.$refs["dropdown"], mobile: false },
        { ref: this.$refs["mobile-favorites"]?.$el, mobile: true },
        { ref: this.$refs["mobile-order-again"]?.$el, mobile: true },
        { ref: this.$refs["my-account"]?.$el, mobile: false },
        { ref: this.$refs["logout"], mobile: false },
        { ref: this.$refs["sign-in"]?.$el, mobile: false },
        { ref: this.$refs["register"]?.$el, mobile: false }
      ];
      //filter elements array
      //e.ref is undefined then it's not render
      //e.ref has class link__disabled then it should not have focus
      //mobile elements filter
      return elements.filter((e) => e.ref && !e.ref?.matches(".link__disabled") && (!e.mobile || !this.isDesktop()));
    },
    handleArrowUp(event) {
      const index = this.elementsList.findIndex((e) => e.ref === event.target);
      //index not found return...
      if (index < 0) return;

      //if button handling flyout close flyout and keep focus
      if (index === 0) {
        this.toggleFlyout = false;
        this.$refs.dropdown.focus();
        return;
      }

      this.elementsList[index - 1].ref.focus();
    },
    async handleArrowDown(event) {
      const index = this.elementsList.findIndex((e) => e.ref === event.target);
      const lastIndex = this.elementsList.length - 1;

      //index not found then return...
      if (index < 0) return;

      //if first element then display flyout
      if (index === 0) {
        this.toggleFlyout = true;

        //need to wait for next tick so flyout is rendered and elements list is recreated
        await this.$nextTick();

        //focus on second element
        this.elementsList[1].ref.focus();
        return;
      }
      //if last element do nothing
      if (index === lastIndex) {
        return;
      }
      this.elementsList[index + 1].ref.focus();
    },
    handleEscape() {
      this.toggleFlyout = false;
      this.$refs.dropdown.focus();
    },
    handleFocusOut(event) {
      //don't hide flyout if focus remains in the profile button or flyout links
      if (!event.relatedTarget || event.relatedTarget.matches(".dropdown__link") || event.relatedTarget.matches(".dropdown")) {
        return;
      }
      this.toggleFlyout = false;
    },
    hideFlyout(event) {
      if (
        !event.target.matches(".dropdown") &&
        !event.target.matches(".navigation-bar__dropdown-button") &&
        !event.target.matches(".fa-user-circle") &&
        !event.target.matches(".navigation-bar__link-text--ml-large") &&
        !event.target.matches(".navigation-bar-salute") &&
        !event.target.matches(".fa-user-circle path")
      ) {
        this.toggleFlyout = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.dropdown {
  cursor: pointer;
  background: none;
  border: none;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  padding: 1px 1px;

  ul {
    padding-inline-start: 0;
    list-style-type: none;
  }

  &-menu {
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.38);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: $dropdown-menu-width;
    min-width: 11.2rem;
    position: absolute;
    transform: translate(-6.7rem, 0.8rem);
    z-index: 10;
    background: $group-3-color-1;
    border-width: 1px;
    border-color: $border-color;
    border-radius: 10px;
    border-style: solid;
    animation-name: open;
    animation-duration: 0.2s;
    animation-fill-mode: forwards;
    animation-timing-function: ease-in-out;
    animation-direction: alternate;

    &::before {
      position: absolute;
      content: "";
      display: inline-block;
      right: 1.5rem;
      top: -0.7em;
      vertical-align: top;
      width: 0;
      height: 0;
      border-radius: 2px;
      border-left: 12px solid transparent;
      border-right: 12px solid transparent;
      border-bottom: 12px solid $group-3-color-1;
      @include respond-to-mobile {
        right: 0.7rem;
      }
    }
  }

  &-salute {
    font-weight: 900;
    padding: 0px 5px 10px ($spacer * 2);
    height: max-content;
  }

  &-container {
    width: 100%;
    padding-bottom: ($spacer * 0.5);
  }

  &-divider {
    border-top: 1px;
    margin: $spacer;
    margin-bottom: ($spacer * 0.75);
  }

  &__link {
    color: $group-3-color-2;
    padding-left: ($spacer * 2);
    height: 48px;
    display: flex;
    align-items: center;
    outline: none;
    background: none;
    border: none;
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    width: 100%;
    box-sizing: border-box;

    &:hover {
      text-decoration: underline;
      background-color: $group-3-color-4-alpha-04;
    }
    &:active {
      background-color: rgba(#000000, 0.1);
    }
  }

  &__link-icon {
    display: flex;
    justify-content: center;
    margin-right: ($spacer * 0.75);
    width: 24px;
  }

  &__container-log-in {
    width: 100%;
    padding-top: ($spacer * 0.5);
    padding-bottom: ($spacer * 0.5);
  }

  &-salute,
  &__favorites,
  &__order-again,
  &-divider {
    display: none;
  }

  @keyframes open {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @include respond-to-mobile {
    .dropdown {
      &-container {
        padding: 31px 0 18px 0;
      }

      &__favorites,
      &__order-again {
        display: flex;
      }

      &-salute,
      &-divider {
        display: block;
      }

      &-menu {
        transform: translate(-10.5rem, 0.8rem);
      }

      &__link {
        color: $group-3-color-2;
      }
    }
  }
}
</style>
